import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, CircleNotch, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo, FAVICON } from "@/lib/brand";
import { Vinti4AdminSection } from "@/components/Vinti4Admin";

function BrandLogoSection() {
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform/brand").then((r) => setVersion(r.data.version)).catch(() => {}); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/platform/brand/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVersion(data.version);
      toast.success("Logótipo atualizado em toda a aplicação");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid="platform-brand-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">Logótipo GESTA CV</h2>
          <p className="text-sm text-muted-foreground">Um único logótipo para toda a aplicação, PDFs, emails e favicon. A versão branca e o ícone “G” são gerados automaticamente.</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary" data-testid="platform-logo-upload">
          {busy ? <CircleNotch className="animate-spin" /> : <UploadSimple size={16} />} Substituir logótipo
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid="platform-logo-input" />
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-white p-4"><Logo version={version} className="h-10 w-auto" testid="preview-logo-color" /></div>
        <div className="flex h-24 items-center justify-center rounded-lg bg-slate-900 p-4"><Logo onDark version={version} className="h-10 w-auto" testid="preview-logo-white" /></div>
        <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] p-4">
          <img src={`${FAVICON}?v=${version}`} alt="favicon" className="h-14 w-14" data-testid="preview-favicon" />
        </div>
      </div>
    </section>
  );
}

export default function PlatformSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/platform/settings").then((r) => setF(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!f) return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/platform/settings", f);
      setF(data);
      toast.success("Configurações guardadas");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="platform-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="rounded-full" asChild><Link to="/plataforma"><ArrowLeft size={18} /></Link></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Super-admin</p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Configurações de pagamento</h1>
          </div>
        </div>
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="save-platform-settings">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
      </div>

      <BrandLogoSection />

      <Link to="/plataforma/planos" className="block rounded-xl border border-dashed border-border bg-card p-4 text-sm hover:bg-secondary" data-testid="goto-platform-plans">
        Os preços, o Preço Fundador e os limites dos planos estão agora em <b>Planos e preços</b> →
      </Link>

      <section className="grid gap-4 rounded-xl border border-border bg-card p-5 md:grid-cols-2">
        <h2 className="font-display text-lg font-bold md:col-span-2">Dados para pagamento</h2>
        <div className="space-y-1.5"><Label>Banco</Label><Input value={f.bank} onChange={set("bank")} data-testid="setting-bank" /></div>
        <div className="space-y-1.5"><Label>Titular</Label><Input value={f.holder} onChange={set("holder")} data-testid="setting-holder" /></div>
        <div className="space-y-1.5"><Label>NIB</Label><Input value={f.nib} onChange={set("nib")} data-testid="setting-nib" /></div>
        <div className="space-y-1.5"><Label>IBAN</Label><Input value={f.iban} onChange={set("iban")} data-testid="setting-iban" /></div>
        <div className="space-y-1.5 md:col-span-2"><Label>Transferência / instruções Vinti4 manual</Label><Textarea rows={3} value={f.vinti4} onChange={set("vinti4")} data-testid="setting-vinti4" /></div>
        <div className="space-y-1.5 md:col-span-2"><Label>Texto livre</Label><Textarea rows={3} value={f.free_text} onChange={set("free_text")} data-testid="setting-free-text" /></div>
      </section>

      <Link to="/plataforma/empresa" className="block rounded-xl border border-dashed border-border bg-card p-4 text-sm hover:bg-secondary" data-testid="goto-platform-company">
        O emissor dos Recibos (nome legal, NIF, morada) está em <b>Dados da empresa</b> →
      </Link>

      <Vinti4AdminSection />
    </div>
  );
}
